import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFa, rp, err, inputCls, Field, Sel } from "./kit";

export function useLists(withVendors = true) {
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [people, setPeople] = useState([]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.map((p) => ({ value: p.id, label: `${p.code ? p.code + " · " : ""}${p.name}` })))).catch(() => {});
    if (withVendors) api.get("/fa/vendors").then((r) => setVendors(r.data.map((v) => ({ value: v.id, label: v.company || v.name })))).catch(() => {});
    api.get("/fa/people").then((r) => setPeople(r.data.map((u) => ({ value: u.id, label: `${u.name}${u.employee_code ? " · " + u.employee_code : ""}` })))).catch(() => {});
  }, [withVendors]);
  return { projects, vendors, people };
}

export function BalanceHint({ partyType, partyId }) {
  const [b, setB] = useState(null);
  useEffect(() => {
    setB(null);
    if (partyId) api.get(`/fa/party-balance?party_type=${partyType}&party_id=${partyId}`).then((r) => setB(r.data)).catch(() => {});
  }, [partyType, partyId]);
  if (!b) return null;
  return (
    <div data-testid="party-balance-hint" className="grid grid-cols-2 gap-2 rounded-lg bg-[#F3ECE2] p-3 text-xs sm:col-span-2 sm:grid-cols-4">
      <div>Unused advance<div className="font-bold">{rp(b.unused_paise)}</div></div>
      <div>Bills awaiting approval<div className="font-bold">{rp(b.pending_bills_paise)}</div></div>
      <div>Outstanding<div className="font-bold">{rp(b.outstanding_paise)}</div></div>
      <div>Carry-forward credit<div className="font-bold text-emerald-700">{rp(b.credit_available_paise)}</div></div>
      {b.credit_available_paise > 0 && <div className="col-span-full text-[11px] text-emerald-800">Available credit can be applied to this project from Carry-Forward & Reconciliation (needs approval).</div>}
    </div>
  );
}

const EMPTY = { kind: "employee_advance", breakdown: [] };

export function RequestForm({ open, onOpenChange, kind = "employee_advance", onSaved }) {
  const me = useFa();
  const { projects, vendors, people } = useLists();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY, kind, party_id: kind === "employee_advance" ? me.uid : "" }); }, [open, kind, me.uid]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const vendorKind = f.kind !== "employee_advance";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, breakdown: f.breakdown.filter((x) => x.item), party_id: f.party_id || null };
      const { data } = await api.post("/fa/requests", body);
      toast.success(`${data.number} submitted to Accounts`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (x) { err(x); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="request-form-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{vendorKind ? "New vendor advance / payment request" : "New advance request"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Request type" req><Sel testid="req-kind" value={f.kind} onChange={(v) => setF((s) => ({ ...s, kind: v, party_id: v === "employee_advance" ? me.uid : "" }))}
            options={[{ value: "employee_advance", label: "Employee advance" }, { value: "vendor_advance", label: "Vendor advance" }]} /></Field>
          <Field label="Project / event" req><Sel testid="req-project" value={f.project_id} onChange={set("project_id")} options={projects} /></Field>
          {vendorKind ? <Field label="Vendor" req><Sel testid="req-vendor" value={f.party_id} onChange={set("party_id")} options={vendors} /></Field>
            : <Field label="Employee (auto)"><Sel testid="req-employee" value={f.party_id} onChange={set("party_id")} options={me.finance ? people : people.filter((p) => p.value === me.uid)} /></Field>}
          <Field label="Expense category" req={!vendorKind}><Sel testid="req-category" value={f.category} onChange={set("category")} options={me.categories} /></Field>
          <BalanceHint partyType={vendorKind ? "vendor" : "employee"} partyId={f.party_id} />
          <Field label="Requested amount (₹)" req><input data-testid="req-amount" inputMode="decimal" className={inputCls} value={f.amount || ""} onChange={set("amount")} /></Field>
          <Field label="Required payment date"><input data-testid="req-date" type="date" className={inputCls} value={f.required_date || ""} onChange={set("required_date")} /></Field>
          <Field label="Project location"><input data-testid="req-location" className={inputCls} value={f.location || ""} onChange={set("location")} /></Field>
          <Field label="Expected settlement date"><input data-testid="req-settlement" type="date" className={inputCls} value={f.expected_settlement || ""} onChange={set("expected_settlement")} /></Field>
          <Field label="Business purpose" req full><input data-testid="req-purpose" className={inputCls} value={f.purpose || ""} onChange={set("purpose")} /></Field>
          <Field label="Detailed justification" req full><textarea data-testid="req-justification" rows={3} className={`${inputCls} h-auto py-2`} value={f.justification || ""} onChange={set("justification")} /></Field>
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between text-xs font-semibold text-zinc-600">Estimated expense breakdown
              <Button type="button" size="sm" variant="outline" data-testid="req-add-line" onClick={() => setF((s) => ({ ...s, breakdown: [...s.breakdown, { item: "", amount: "" }] }))}><Plus className="mr-1 h-3.5 w-3.5" />Line</Button></div>
            {f.breakdown.map((l, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`req-line-item-${i}`} placeholder="Item" className={inputCls} value={l.item} onChange={(e) => setF((s) => ({ ...s, breakdown: s.breakdown.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)) }))} />
                <input data-testid={`req-line-amount-${i}`} placeholder="₹" inputMode="decimal" className={`${inputCls} w-36`} value={l.amount} onChange={(e) => setF((s) => ({ ...s, breakdown: s.breakdown.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) }))} />
                <button type="button" onClick={() => setF((s) => ({ ...s, breakdown: s.breakdown.filter((_, j) => j !== i) }))} className="text-zinc-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 sm:col-span-2">Attach quotations, bookings or supporting documents from the request after submitting.</div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="req-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !f.project_id || !f.amount || !f.purpose || !f.justification || !f.party_id} data-testid="req-submit">{busy ? "Submitting…" : "Submit request"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
